import { useState, useCallback, useEffect } from 'react';
import * as React from 'react';
import {
    GraphQLTaggedNode,
    fetchQuery_DEPRECATED as fetchQuery,
    OperationType,
    IEnvironment,
} from 'relay-runtime';
import { SingleSelect, SelectOption } from 'dibs-elements/exports/SingleSelect';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback';

// TODO: move to exports folder
import { CommonSelectProps, NamedSelectOption } from 'dibs-elements/src/Common/Select/sharedTypes';

type Props<T extends OperationType> = CommonSelectProps & {
    value: string;
    placeholder: string;
    relayEnvironment: IEnvironment;
    queryVariables?: T['variables'];
    debounce?: number;
    query: GraphQLTaggedNode;
    getOptions: (queryResponse: T['response']) => SelectOption[];
    onChange: (option: NamedSelectOption) => void;
    noResultsPrompt?: React.ReactNode;
    minSearchLength?: number;
    onFetch?: (payload: { response: T['response']; error: Error | null }) => void;
};

const TypeaheadSelect = <T extends OperationType>(props: Props<T>): React.ReactElement => {
    const {
        name,
        value,
        options: optionsProp,
        relayEnvironment,
        queryVariables,
        query,
        debounce = 500,
        getOptions,
        onChange,
        noResultsPrompt = null,
        minSearchLength = 0,
        onFetch: onFetchProp = () => {},
        ...otherProps
    } = props;
    const [selectValue, setValue] = useState(value);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [options, setOptions] = useState(optionsProp || []);

    useEffect(() => {
        if (optionsProp) {
            setOptions(optionsProp);
        }
    }, [optionsProp]);

    useEffect(() => {
        setValue(value);
    }, [value]);

    const onFetch = useCallback(onFetchProp, []);

    const fetchData = useCallback(
        (val: string) => {
            setIsLoading(true);
            setErrorMessage('');
            setOptions([]);
            fetchQuery<T>(relayEnvironment, query, {
                ...(queryVariables || {}),
                value: val,
            })
                .then(queryResponse => {
                    setOptions(getOptions(queryResponse));
                    setIsLoading(false);
                    onFetch({ response: queryResponse, error: null });
                })
                .catch((err: Error) => {
                    // eslint-disable-next-line no-console
                    console.error('err', err);
                    setErrorMessage(err.toString());
                    setIsLoading(false);
                    onFetch({ response: null, error: err });
                });
        },
        [query, queryVariables, relayEnvironment, getOptions, onFetch]
    );

    const [debouncedFetchData] = useDebouncedCallback(fetchData, debounce);

    function handleInputChange(selectInputValue: string): void {
        if (selectInputValue && selectInputValue.length >= minSearchLength) {
            debouncedFetchData(selectInputValue);
        }
    }

    return (
        <SingleSelect
            searchable
            name={name}
            value={selectValue}
            options={options}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            errorMessage={errorMessage}
            noResultsPrompt={noResultsPrompt}
            onChange={payload => {
                /**
                 * when clearing select it sends `null` as a payload
                 * not very convenient to modify reducers value, so
                 * updated onChange functionality to still send `name` of the field
                 * which has to be updated but with `value` of empty string
                 */
                if (payload === null) {
                    onChange({ name, label: '', value: '' });
                } else {
                    onChange(payload);
                }
            }}
            {...otherProps}
        />
    );
};

export default TypeaheadSelect;
